import { combineReducers } from 'redux';
import * as R from 'ramda';
import { EventForUserDto, EventForAdminDto } from './types';

import type { FetchEventsAction } from '../social/actions';
import type {
  ReceiveEventsAction,
  ReceiveEventAction,
  CancelEventAction,
  CreateEventAction,
  UpdateEventAction,
  AttendEventAction,
  AbsentEventAction,
  DeleteEventAction,
  DuplicateEventAction,
  ClearNetworkCountsAction,
  ReceiveAdminEventsAction,
} from './actions';

import type { Event, EventSimpleDto, EventUserScopeFragment, EventAdminScopeFragment } from './types';
import type { StoreState } from '../../common/types/store';
import type { TypeOfItemFromArray } from '../../common/types/util-types';

type DefaultRelatedAction = {
  type: 'any_string',
  related?: {
    events?: EventSimpleDto[];
  };
};

type ExternalRelatedActions =
  | FetchEventsAction;

type OwnRelatedActions =
  | ReceiveEventsAction
  | ReceiveEventAction
  | AttendEventAction
  | CancelEventAction
  | CreateEventAction
  | UpdateEventAction
  | AbsentEventAction
  | DeleteEventAction
  | DefaultRelatedAction
  | DuplicateEventAction
  | ClearNetworkCountsAction;

export type ReducerRelatedActionTypes = ExternalRelatedActions | OwnRelatedActions;

type CommonItemsState = Record<string, EventSimpleDto>;
type AllItemStates = CommonItemsState | AdminScopeState | UserScopeState;

// Scope keys below are type-bound. Don't change them until you are changing Event types.
export const eventAdminScopeKeys = <const>[
  'audience',
  'going_users',
];

export const eventUserScopeKeys = <const>[
  'going_users',
  'invited_users_count',
  'remaining_spots_count',
  'is_going',
  'registration_deadline',
];

const omitAdminScope = (item: EventForAdminDto) => R.omit(eventAdminScopeKeys, item);
const omitUserScope = (item: EventForUserDto) => R.omit(eventUserScopeKeys, item);
const pickAdminScope = (item: EventForAdminDto) => R.pick(eventAdminScopeKeys, item);
const pickUserScope = (item: EventForUserDto): EventUserScopeFragment => ({
  ...R.pick(eventUserScopeKeys, item),
  registration_deadline:
    item.registration_deadline ?? item.settings?.registration_deadline ?? null,
});

type ReceiveItemsReturnType<I, S, F> = (
  I extends Array<any>
    ? F extends (i: never) => any
      ? Record<string, ReturnType<F>>
      : Record<string, TypeOfItemFromArray<I>>
    : S
  );

const receiveItems = <I, S extends AllItemStates, F>(
  items: I,
  state: S,
  processItem?: F,
): ReceiveItemsReturnType<I, S, F> => {
  if (Array.isArray(items)) {
    return items.reduce((acc, item) => R.assoc(
      item.id,
      typeof processItem === 'function' ? processItem(item) : item,
      acc,
    ), state);
  }

  return state as ReceiveItemsReturnType<never, S, never>;
};

const sharedItemsCases = <
  S,
  A extends ReducerRelatedActionTypes,
  >(state: S, action: A) => {
  switch (action.type) {
    case 'events/CANCEL_EVENT':
    case 'events/DELETE_EVENT':
      return R.omit([action.eventId], state);
    default:
      return state;
  }
};

const commonItems = (state: CommonItemsState = {}, action: ReducerRelatedActionTypes): typeof state => {
  switch (action.type) {
    case 'events/user/RECEIVE_EVENTS': {
      // Skip updating items if we're only fetching counts
      const userAction = action as import('./actions').ReceiveUserEventsAction;
      if (userAction.countsOnly) {
        return state;
      }
      return receiveItems(action.items, state, omitUserScope);
    }
    case 'events/admin/RECEIVE_EVENTS': {
      // Skip updating items if we're only fetching counts
      const adminAction = action as import('./actions').ReceiveAdminEventsAction;
      if (adminAction.countsOnly) {
        return state;
      }
      return receiveItems(action.items, state, omitAdminScope);
    }
    case 'events/network/RECEIVE_EVENTS':
      return receiveItems(action.items, state, omitAdminScope);
    case 'social/RECEIVE_EVENTS':
      return receiveItems(action.items, state, omitUserScope);
    case 'events/RECEIVE_EVENT':
      return receiveItems([action.item], state, omitAdminScope);
    case 'events/UPDATE_EVENT':
    case 'events/CREATE_EVENT':
      return receiveItems([action.item], state, omitAdminScope);
    case 'events/DUPLICATE_EVENT':
      return {
        ...state,
        ...receiveItems([action.response.data], state, omitAdminScope),
      };
    default:
      if ('related' in action && action.related?.events) {
        return action.related.events.reduce((acc, item) => R.assoc(item.id, item, acc), state);
      }

      return sharedItemsCases(state, action);
  }
};

type AdminScopeState = Record<string, EventAdminScopeFragment>;
const adminScope = (state: AdminScopeState = {}, action: ReducerRelatedActionTypes): typeof state => {
  switch (action.type) {
    case 'events/admin/RECEIVE_EVENTS': {
      // Skip updating admin scope if we're only fetching counts
      const adminAction = action as import('./actions').ReceiveAdminEventsAction;
      if (adminAction.countsOnly) {
        return state;
      }
      return receiveItems(action.items, state, pickAdminScope);
    }
    case 'events/network/RECEIVE_EVENTS':
      return receiveItems(action.items, state, pickAdminScope);
    case 'events/RECEIVE_EVENT':
    case 'events/UPDATE_EVENT':
    case 'events/CREATE_EVENT': {
      const existing = state[action.item.id];
      const incoming = pickAdminScope(action.item as EventForAdminDto);
      const merged = { ...(existing || {}) };
      for (const key of eventAdminScopeKeys) {
        const v = incoming[key as keyof EventAdminScopeFragment];
        if (v !== undefined && v !== null) (merged as Record<string, unknown>)[key] = v;
      }
      return { ...state, [action.item.id]: merged };
    }
    case 'events/DUPLICATE_EVENT':
      return {
        ...state,
        ...receiveItems([action.response.data], state, pickAdminScope)
      };
    case 'events/ATTEND_EVENT': {
      const currentEvent = state[action.id];
      if (!currentEvent) return state;

      const newState = {
        ...state,
        [action.id]: {
          ...currentEvent,
          going_users: {
            count: (currentEvent.going_users?.count || 0) + 1,
            users_preview: [
              ...(currentEvent.going_users?.users_preview || []),
              action.user,
            ],
          },
        },
      };
      return newState;
    }
    case 'events/ABSENT_EVENT': {
      const currentEvent = state[action.id];
      if (!currentEvent) return state;

      const newState = {
        ...state,
        [action.id]: {
          ...currentEvent,
          going_users: {
            count: (currentEvent.going_users?.count || 1) - 1,
            users_preview: (currentEvent.going_users?.users_preview || []).filter(({ id }) => id !== action.userId),
          },
        },
      };
      return newState;
    }
    default:
      return sharedItemsCases(state, action);
  }
};

type UserScopeState = Record<string, EventUserScopeFragment>;
const userScope = (state: UserScopeState = {}, action: ReducerRelatedActionTypes): typeof state => {
  switch (action.type) {
    case 'events/user/RECEIVE_EVENTS': {
      // Skip updating user scope if we're only fetching counts
      const userAction = action as import('./actions').ReceiveUserEventsAction;
      if (userAction.countsOnly) {
        return state;
      }
      return receiveItems(action.items, state, pickUserScope);
    }
    case 'social/RECEIVE_EVENTS':
      return receiveItems(action.items, state, pickUserScope);
    case 'events/RECEIVE_EVENT':
      return receiveItems([action.item], state, pickUserScope);
    case 'events/DUPLICATE_EVENT':
      return {
        ...state,
        ...receiveItems([action.response.data], state, pickUserScope)
      };
    case 'events/ATTEND_EVENT': {
      const currentEvent = state[action.id];
      if (!currentEvent) return state;

      return {
        ...state,
        [action.id]: {
          ...currentEvent,
          is_going: true,
          going_users: {
            count: (currentEvent.going_users?.count || 0) + 1,
            users_preview: [
              ...(currentEvent.going_users?.users_preview || []),
              action.user,
            ],
          },
        },
      };
    }
    case 'events/ABSENT_EVENT': {
      const currentEvent = state[action.id];
      if (!currentEvent) return state;

      return {
        ...state,
        [action.id]: {
          ...currentEvent,
          is_going: false,
          going_users: {
            count: (currentEvent.going_users?.count || 1) - 1,
            users_preview: (currentEvent.going_users?.users_preview || []).filter(({ id }) => id !== action.userId),
          },
        },
      };
    }
    default:
      return sharedItemsCases(state, action);
  }
};

const ids = (state: string[] = [], action: ReducerRelatedActionTypes): typeof state => {
  switch (action.type) {
    case 'events/user/RECEIVE_EVENTS': {
      // Skip updating ids if we're only fetching counts
      const userAction = action as import('./actions').ReceiveUserEventsAction;
      if (userAction.countsOnly) {
        return state;
      }
      if (userAction.strategy === 'append') {
        return [...state, ...userAction.items.map((item) => item.id)];
      }
      return userAction.items.map((item) => item.id);
    }
    case 'events/admin/RECEIVE_EVENTS': {
      // Skip updating ids if we're only fetching counts
      const adminAction = action as import('./actions').ReceiveAdminEventsAction;
      if (adminAction.countsOnly) {
        return state;
      }
      if (adminAction.strategy === 'append') {
        return [...state, ...adminAction.items.map((item) => item.id)];
      }
      return adminAction.items.map((item) => item.id);
    }
    case 'events/RECEIVE_EVENT':
      // Detail fetch should update item data only; do not replace the list ids.
      return state.includes(action.item.id) ? state : [...state, action.item.id];
    case 'events/CREATE_EVENT':
      return state.includes(action.item.id) ? state : [...state, action.item.id];
    case 'events/DUPLICATE_EVENT':
      return state.map((id: string) => {
        if (id === action.originalEventId) {
          // let's put the duplicated event right before the original event, so
          // the positioning in the UI matches the order returned by the API
          return [action.response.data.id, id];
        }
        return id;
      }).flat();
    case 'events/CANCEL_EVENT':
      return state.filter((event) => event !== action.eventId);
    default:
      return state;
  }
};

const available = (state: string[] = [], action: ReducerRelatedActionTypes): typeof state => {
  switch (action.type) {
    case 'social/RECEIVE_EVENTS':
      return action.items.map((item) => item.id);
    case 'events/ABSENT_EVENT':
      return state.filter((id) => id !== action.id);
    default:
      return state;
  }
};

const userCountsInitialState = {
  upcoming: 0,
  past: 0,
  draft: 0,
  canceled: 0,
};

const adminCountsInitialState = {
  all: 0,
  live: 0,
  draft: 0,
  canceled: 0,
  upcoming: 0,
  past: 0,
};

// User counts for Attend section (upcoming, past)
const userCounts = (
  state = userCountsInitialState,
  action: ReducerRelatedActionTypes
): typeof state => {
  switch (action.type) {
    case 'events/user/RECEIVE_EVENTS':
    case 'social/RECEIVE_EVENTS':
      return {
        ...state,
        ...action.counts,
      };
    default:
      return state;
  }
};

// Admin counts for Build & manage section at organisation level (all, live, draft, canceled)
const adminCounts = (
  state = adminCountsInitialState,
  action: ReducerRelatedActionTypes
): typeof state => {
  switch (action.type) {
    case 'events/admin/RECEIVE_EVENTS': {
      const adminAction = action as ReceiveAdminEventsAction;
      if (adminAction.storeNetworkId == null && adminAction.networkId == null) {
        return {
          ...state,
          ...adminAction.counts,
        };
      }
      return state;
    }
    case 'events/CANCEL_EVENT':
      return {
        ...state,
        canceled: state.canceled + 1,
        all: Math.max(0, state.all - 1),
      };
    case 'events/DELETE_EVENT':
      return {
        ...state,
        all: Math.max(0, state.all - 1),
      };
    default:
      return state;
  }
};

// Network counts for Build & manage section at network level (all, live, draft, canceled)
// Stored by networkId to keep counts separate for each network
type NetworkCountsState = Record<string, typeof adminCountsInitialState>;
const networkCounts = (
  state: NetworkCountsState = {},
  action: ReducerRelatedActionTypes
): typeof state => {
  switch (action.type) {
    case 'events/admin/RECEIVE_EVENTS': {
      const adminAction = action as ReceiveAdminEventsAction;
      const key = adminAction.storeNetworkId ?? adminAction.networkId;
      if (key != null) {
        return {
          ...state,
          [String(key)]: {
            ...adminCountsInitialState,
            ...adminAction.counts,
          },
        };
      }
      return state;
    }
    case 'events/network/RECEIVE_EVENTS': {
      const networkId = action.networkId || 'default';
      return {
        ...state,
        [networkId]: {
          ...adminCountsInitialState,
          ...action.counts,
        },
      };
    }
    case 'events/CLEAR_NETWORK_COUNTS': {
      const { networkId: keyToRemove } = action as ClearNetworkCountsAction;
      return R.omit([keyToRemove], state);
    }
    default:
      return state;
  }
};

// Keep counts for backward compatibility (will be removed later)
const counts = (
  state = userCountsInitialState,
  action: ReducerRelatedActionTypes
): typeof state => {
  switch (action.type) {
    case 'events/admin/RECEIVE_EVENTS':
      // Admin counts don't have upcoming/past, so we keep the state for those
      return {
        ...state,
        draft: action.counts.draft ?? state.draft,
        canceled: action.counts.canceled ?? state.canceled,
      };
    case 'events/user/RECEIVE_EVENTS':
    case 'social/RECEIVE_EVENTS':
      return {
        ...state,
        ...action.counts,
      };
    case 'events/CANCEL_EVENT':
      return {
        ...state,
        canceled: state.canceled + 1,
        upcoming: Math.max(0, state.upcoming - 1),
      };
    case 'events/DELETE_EVENT':
      return {
        ...state,
        upcoming: Math.max(0, state.upcoming - 1),
      };
    default:
      return state;
  }
};

// @ts-expect-error
export const findById = (state: StoreState, id: string): Event => state.events.items.common[id];

const items = combineReducers({
  common: commonItems,
  admin: adminScope,
  user: userScope,
});

const eventsReducer = combineReducers({
  items,
  ids,
  available,
  counts,
  userCounts,
  adminCounts,
  networkCounts,
});

export default eventsReducer;
